/*
 * How a long breakdown is shortened for reading: shared by the Positions card
 * on Portfolio and the underlying exposure panel on Risk & liquidity, which
 * both draw a donut beside a ledger of the same rows.
 */

/** Ledger and donut cap; the tail aggregates into one reconciled row. */
const MAX_LEDGER_ROWS = 7;
/**
 * Rows below this share of the total read as noise in a long list — they
 * collapse into the aggregate row whenever at least one real row exists.
 */
const DUST_SHARE_PCT = 0.5;

/**
 * Two declutter passes share one aggregate tail row: dust collapses whenever
 * real rows exist beside it, and long lists cap at MAX_LEDGER_ROWS. A
 * population that is entirely dust stays listed — aggregating everything
 * into one row would erase the whole picture. Expects `entries` sorted by
 * value descending.
 */
export function collapseTail<T extends { share: number }>(
  entries: T[],
  makeOther: (tail: T[]) => T
): T[] {
  const majors = entries.filter((entry) => entry.share >= DUST_SHARE_PCT);
  const dust = entries.filter((entry) => entry.share < DUST_SHARE_PCT);
  let head = entries;
  let tail: T[] = [];
  if (majors.length > 0 && dust.length >= 2) {
    head = majors.slice(0, MAX_LEDGER_ROWS);
    tail = [...majors.slice(MAX_LEDGER_ROWS), ...dust];
  } else if (entries.length > MAX_LEDGER_ROWS + 1) {
    head = entries.slice(0, MAX_LEDGER_ROWS);
    tail = entries.slice(MAX_LEDGER_ROWS);
  }
  return tail.length > 0 ? [...head, makeOther(tail)] : head;
}
